import React, { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import VisibilitySensor from 'react-visibility-sensor';

import { Box, Flex, Grid, Paragraph } from '../UIKit/system';
import { OFFSET_SIDE, STANDARD_PADDING } from '../../constants';
import Container from '../container';
import StageContent from './stage-content';
import { vw } from '../../utils/size-functions';
import Title from '../title';
import mq from '../../utils/mq';
import VideoAutoplay from '../video-autoplay';
import { useResponsiveContext } from '../responsive';
import ReactPlayer from 'react-player/lazy';
import { useInView } from 'react-intersection-observer';
import { AnchorPoint } from '../anchor-point';

const StyledSectionStagesOuter = styled(Box)`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SectionGrid = styled(Grid).attrs(() => ({
  gridAutoFlow: ['row', 'column'],
  gridTemplateColumns: [undefined, 'repeat(2, 1fr)'],
  gridColumnGap: [0, 50]
}))``;

const StyledGrid = styled(Grid).attrs(() => ({
  alignContent: 'center'
}))``;

const Col = styled.div`
  width: ${vw(616)};
  flex-shrink: 0;
  padding-right: 30px;

  ${mq.mobile(css`
    width: 100%;
    padding-right: 0;
  `)};
`;

const Content = styled(Box).attrs(() => ({
  py: [0, 0, 30, 50]
}))``;

const VideoContainer = styled.div`
  width: calc(100% + ${OFFSET_SIDE}px);
  max-width: 710px;
  overflow: hidden;
  border-radius: var(--Radius-Radius-3, 16px) 0 0 var(--Radius-Radius-3, 16px);
  ${mq.containerWidthMin(css`
    position: absolute;
    right: 0;
    width: 50%;
  `)}

  ${mq.mobile(css`
    display: none;
  `)};
`;

const StyledVideoAutoplay = styled(VideoAutoplay)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const StyledCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  padding-right: 0px;
  @media (min-width: 2000px) {
    justify-content: center;
  }
`;

function SectionStages({ title, text, stages, totalLength, video, anchor }) {
  const [activeStage, setActiveStage] = useState(0);
  const [videoCanPlay, setVideoCanPlay] = useState(false);
  const [playerPlaying, setPlayerPlaying] = useState(true);
  const [visible, setVisible] = useState(false);
  const [doAnimate, setDoAnimate] = useState(false);
  const videoNode = useRef(null);
  const { isDesktopOrTablet } = useResponsiveContext();
  const [isInView, setIsInView] = useState(false);

  const [ref, inView] = useInView({
    threshold: 0.45
  });

  const videoRef = useRef();

  const videoDuration = videoNode.current ? videoNode.current.duration : 0;
  useEffect(() => {
    if (!videoNode.current || !videoCanPlay) {
      return;
    }
    videoNode.current.currentTime = 0;
    videoNode.current.play();
  }, [activeStage, videoCanPlay]);

  useEffect(() => {
    if (!videoNode.current) {
      return;
    }
    visible ? videoNode.current.play() : videoNode.current.pause();
  }, [visible, isDesktopOrTablet]);

  function handleVideoEnded() {
    setActiveStage(getNextStageIndex());
  }

  function handleVisibilityChange(visible) {
    setVisible(visible);
  }

  function getNextStageIndex() {
    return activeStage >= stages.length - 1 ? 0 : activeStage + 1;
  }

  function getCurrentVideoSrc() {
    return stages[activeStage].video;
  }

  function handlePlaying() {
    setDoAnimate(true);
  }

  function handleEnded() {
    videoRef.current.seekTo(0);
    setActiveStage(0);
    setPlayerPlaying(false);
    setDoAnimate(false);
    setTimeout(() => {
      setPlayerPlaying(true);
      setDoAnimate(true);
    }, 1);
  }

  useEffect(() => {
    if (inView) {
      setIsInView(true);
    }
  }, [inView]);

  useEffect(() => {
    if (isInView) {
      setIsInView(true);
      setPlayerPlaying(true);
    }
  }, [isInView]);

  return (
    <VisibilitySensor
      onChange={handleVisibilityChange}
      partialVisibility
      minTopValue={200}
    >
      <StyledSectionStagesOuter>
        <AnchorPoint anchor={anchor} />
        <Container pt={88} pb={STANDARD_PADDING} ref={ref}>
          <SectionGrid>
            <StyledGrid>
              <Content>
                <Title mb={68} fontSizeMedium html={title} />
                {stages.map((stage, index) => {
                  let delay = 0;
                  let lengthUpd = stage.length / 1000;
                  if (index !== 0) {
                    let priorStages = stages.slice(0, index);
                    priorStages.map((s, i) => {
                      delay = delay + parseInt(s.length);
                    });
                  }

                  return (
                    <StageContent
                      key={index}
                      delay={delay}
                      playerPlaying={playerPlaying}
                      videoDuration={videoDuration}
                      totalLength={totalLength}
                      doAnimate={doAnimate}
                      visible={visible}
                      video={stage.video}
                      lengthSeconds={lengthUpd}
                      {...stage}
                    />
                  );
                })}
                <Paragraph grey fontSize={15} html={text} />
              </Content>
            </StyledGrid>
            {isDesktopOrTablet && (
              <VideoContainer>
                <ReactPlayer
                  url={video}
                  ref={videoRef}
                  width={'100%'}
                  height={'100%'}
                  playing={playerPlaying}
                  controls={false}
                  playsinline={true}
                  muted={true}
                  onPlay={() => handlePlaying()}
                  onEnded={() => handleEnded()}
                  vimeoConfig={{
                    iframeParams: { fullscreen: 0 },
                    playsinline: true
                  }}
                />
              </VideoContainer>
            )}
          </SectionGrid>
        </Container>
      </StyledSectionStagesOuter>
    </VisibilitySensor>
  );
}

export default SectionStages;
